import React, { useEffect, useState } from 'react'
import { useAuthState, useQuery } from 'packages/core'
import { Loader } from 'packages/eid-ui'
import {
    useAccessSubcomponents,
    useTranslations,
    useCurrentPerson,
    useMyIdConfigSettings,
    useMyIdConfigSetting,
} from 'hooks'
import moment from 'moment'
import i18n from 'i18next'
import { useAppState } from 'appContext'

const useLoadAccessSubcomponents = () => {
    const [{ access }, dispatchAuthState] = useAuthState()
    const { data } = useAccessSubcomponents()

    useEffect(() => {
        if (data) {
            dispatchAuthState({
                type: 'SET_ACCESS_SUBCOMPONENTS',
                payload: data.data,
            })
        }
    }, [data, dispatchAuthState])

    return access !== null
}

const useSearchToLoadConfig = () => {
    const [, dispatchAppState] = useAppState()
    const { data: myIdSearchToLoadConfig } =
        useMyIdConfigSetting('SearchConfig')

    useEffect(() => {
        if (myIdSearchToLoadConfig) {
            dispatchAppState({
                type: 'SET_SEARCH_TO_LOAD_CONFIG',
                payload: myIdSearchToLoadConfig,
            })
        }
    }, [myIdSearchToLoadConfig, dispatchAppState])

    return myIdSearchToLoadConfig
}

const useLoadTranslations = () => {
    const RESOURCE_SET_NAME = 'MyIdentity'

    const query = useQuery()
    // eslint-disable-next-line
    const [{ debugLocalization: isLocalized }, dispatchAppState] = useAppState()

    const debugLocalization =
        query.get('debugLocalization') === 'true' || isLocalized

    const { data } = useTranslations()
    const { data: currentPerson } = useCurrentPerson()
    const [refresh, setRefresh] = useState(false)

    const locale =
        currentPerson && currentPerson.language?.languageCode
            ? currentPerson.language?.languageCode
            : 'en'
    useEffect(() => {
        i18n.changeLanguage(locale)
        moment.locale(locale)
    }, [locale])

    useEffect(() => {
        if (query.get('debugLocalization') === 'true') {
            dispatchAppState({
                type: 'SET_DEBUG_LOCALE',
                payload: true,
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (data) {
            const localeData = data.reduce((current, r) => {
                let keyToUse = r.key
                const parts = r.key.split('_')
                if (parts[0] === RESOURCE_SET_NAME) {
                    keyToUse = r.key.substring(RESOURCE_SET_NAME.length + 1)
                }

                if (debugLocalization) {
                    current[keyToUse] = r.key
                } else {
                    current[keyToUse] = r.translatedValue
                }

                return current
            }, {})

            i18n.addResourceBundle(locale, 'translation', localeData)
        }
    }, [data, locale, debugLocalization])

    const isResourceBundleReadyToUse =
        i18n.getResourceBundle(locale) !== undefined

    if (Boolean(data) && !isResourceBundleReadyToUse) {
        setTimeout(() => {
            setRefresh(!refresh)
        }, [1000])
    }
    return Boolean(data) && isResourceBundleReadyToUse
}

const useLoadGlobalEidSettings = () => {
    const { data: settingsDetails } = useMyIdConfigSettings([
        'MyID_Hide_Edit_Button_Via_Workflow',
        'MyID_Show_Edit_Buttons_Via_Profile_Sections',
    ])

    return Boolean(settingsDetails)
}

const withRequiredData = (ChildComponent) => (props) => {
    const translationsLoaded = useLoadTranslations()
    const accessSubcomponentsLoaded = useLoadAccessSubcomponents()
    const globalConfigLoaded = useLoadGlobalEidSettings()
    const searchToLoadConfigLoaded = useSearchToLoadConfig()
    if (
        !accessSubcomponentsLoaded ||
        !translationsLoaded ||
        !globalConfigLoaded ||
        !searchToLoadConfigLoaded
    ) {
        return <Loader />
    } else {
        return <ChildComponent {...props} />
    }
}

export default withRequiredData
